import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Heart } from "lucide-react";
import clsx from "clsx";
import { Api } from "../lib/api.js";
import { getActiveProfileId } from "../lib/profile.js";

/**
 * Toggleable favorite (was: bookmark) icon. Lives on library cards (overlay)
 * and in the Watch page sidebar (chip form). Uses optimistic updates against
 * the cached pins list so toggling feels instant. Internally still calls the
 * pins API — that's the durable name; the UI just speaks "favorite" now.
 */
export function BookmarkButton({
  itemId,
  variant = "overlay",
  stopPropagation = true,
}: {
  itemId: string;
  variant?: "overlay" | "inline";
  stopPropagation?: boolean;
}): JSX.Element | null {
  const profileId = getActiveProfileId();
  const qc = useQueryClient();
  const pinsQ = useQuery({
    queryKey: ["pins", profileId],
    queryFn: () => Api.library.pins(profileId!),
    enabled: !!profileId,
    staleTime: 60_000,
  });

  const isPinned = !!pinsQ.data?.items.some((p) => p.id === itemId);

  const toggle = useMutation({
    mutationFn: async () => {
      if (!profileId) return;
      if (isPinned) {
        await Api.library.unpin(profileId, itemId);
      } else {
        await Api.library.pin({ profile_id: profileId, item_id: itemId });
      }
    },
    onMutate: async () => {
      if (!profileId) return;
      await qc.cancelQueries({ queryKey: ["pins", profileId] });
      const prev = qc.getQueryData<{ items: { id: string }[] }>(["pins", profileId]);
      qc.setQueryData(["pins", profileId], (old: { items: { id: string }[] } | undefined) => {
        if (!old) return { profile_id: profileId, items: isPinned ? [] : [{ id: itemId } as { id: string }] };
        return {
          ...old,
          items: isPinned
            ? old.items.filter((p) => p.id !== itemId)
            : [...old.items, { id: itemId } as { id: string }],
        };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev && profileId) qc.setQueryData(["pins", profileId], ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["pins", profileId] });
      void qc.invalidateQueries({ queryKey: ["recs", profileId] });
    },
  });

  if (!profileId) return null;

  const handle = (e: React.MouseEvent): void => {
    if (stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    toggle.mutate();
  };

  if (variant === "inline") {
    return (
      <button
        onClick={handle}
        aria-pressed={isPinned}
        className={clsx(
          "pill-ghost inline-flex items-center gap-2",
          isPinned && "text-goldHi",
        )}
        disabled={toggle.isPending}
      >
        <Heart size={14} fill={isPinned ? "currentColor" : "transparent"} />
        {isPinned ? "Favorited" : "Favorite"}
      </button>
    );
  }

  return (
    <button
      onClick={handle}
      aria-pressed={isPinned}
      aria-label={isPinned ? "Remove favorite" : "Add to favorites"}
      title={isPinned ? "Remove favorite" : "Favorite this"}
      className={clsx(
        "absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ease-spring",
        "bg-ink/70 backdrop-blur-sm border",
        isPinned
          ? "border-goldHi text-goldHi shadow-vermilionGlow"
          : "border-hairline text-parchment opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-gold hover:border-gold",
      )}
      disabled={toggle.isPending}
    >
      <Heart size={14} fill={isPinned ? "currentColor" : "transparent"} />
    </button>
  );
}
